// src/components/Auth/SignUpModal.jsx
// Sign up modal for converting anonymous users to authenticated

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../hooks/useAuth';
import './AuthModal.css';

// Detect if running in native Capacitor app
function isNativeApp() {
  return !!window?.Capacitor?.isNativePlatform?.();
}

export default function SignUpModal({ isOpen, onClose, onSuccess, defaultToSignIn = false }) {
  const { signUp, signIn } = useAuth();
  // Native apps default to Sign In mode (users sync existing subscriptions)
  const [isSignUp, setIsSignUp] = useState(!defaultToSignIn && !isNativeApp());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        await signUp(email, password);
      } else {
        await signIn(email, password);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('[Auth] Error:', err);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (code) => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Try logging in instead.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  // Use portal to render at document body level, avoiding stacking context issues
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>

        <div className="modal-header">
          <h2>{isSignUp ? 'Create Your Account' : 'Welcome Back'}</h2>
          <p>
            {isSignUp
              ? (isNativeApp() ? 'Sign up to sync your trips across devices' : 'Sign up to unlock Pro features')
              : (isNativeApp() ? 'Sign in to sync your subscription' : 'Sign in to access your Pro account')}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              minLength={6}
            />
            {isSignUp && (
              <small className="hint">At least 6 characters</small>
            )}
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading
              ? 'Loading...'
              : isSignUp
              ? 'Create Account'
              : 'Sign In'}
          </button>
        </form>

        <div className="auth-toggle">
          <p>
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
              }}
            >
              {isSignUp ? 'Sign In' : 'Sign Up'}
            </button>
          </p>
        </div>

        <div className="auth-footer">
          <p className="terms-text">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
